import { useState } from 'react';
import { IconLogout, IconMapPin, IconMessageReport } from '@tabler/icons-react';
import { PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import '../common.i18n';
import { Button, Segmented, Skeleton, StateView, useToast } from '@hana/ui';
import { StateCard, meeStateProps } from '../meeState';
import type { DayStatus } from '../data';
import { dm, dowOf, fdw, num, todayCard } from '../home/view';
import { RequestDrawer } from '../requests/RequestDrawer';
import { useMee } from '../useMee';
import s from '../mee.module.css';
import { monthGrid, monthKpi, shiftMonth, type MonthCell } from './month';

const DOWS = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'];
interface Look { k: string; bar: string; chipBg?: string; chipTx?: string }
// Màu / nhãn theo `SC` của board Mint-Mee (nhãn ngắn); ngày nghỉ / chưa tới không vẽ thanh.
const LOOK: Record<DayStatus, Look> = {
  ok: { k: 'Đủ công', bar: 'var(--ok)', chipBg: 'var(--ok-bg)', chipTx: 'var(--ok)' },
  short: { k: 'Thiếu', bar: 'var(--d-amber)', chipBg: 'var(--warn-bg)', chipTx: 'var(--warn)' },
  leave: { k: 'Phép', bar: 'var(--d-blue)' },
  halfLeave: { k: 'Nửa phép', bar: 'var(--d-blue)' },
  holiday: { k: 'Lễ', bar: 'var(--ok-dot)' },
  off: { k: 'Nghỉ', bar: 'transparent' },
  future: { k: '', bar: 'transparent' },
  today: { k: 'Hôm nay', bar: 'var(--pri)' },
};
const LEGEND: Array<[string, string]> = [['Đủ', 'var(--ok)'], ['Thiếu', 'var(--d-amber)'], ['Phép', 'var(--d-blue)'], ['Lễ', 'var(--ok-dot)'], ['Nghỉ', 'var(--bd)']];
const MARK: Partial<Record<DayStatus, string>> = { short: '!', halfLeave: '½', holiday: 'L' };
const hours = (n: number) => `${num(n)} giờ`;
const h1 = (n: number) => `${num(Math.round(n * 10) / 10)}h`;
const SPARK_MAX = 11;

function TimeBody({ m, ym, fix }: { m: ReturnType<typeof useMee>; ym: string; fix: (day: string) => void }) {
  const t = useT();
  const [pick, setPick] = useState(m.today);
  const cells = monthGrid(m.cal, ym, m.today, m.attendance);
  const kpi = monthKpi(m.cal, ym, cells);
  const sel: MonthCell = cells.find((c) => c.day === pick && c.inMonth) ?? cells.find((c) => c.day === m.today && c.inMonth) ?? cells.find((c) => c.inMonth)!;
  const live = sel.rec.status === 'today' ? todayCard(m.cal, sel.day, m.now, m.attendance.find((a) => a.day === sel.day)).presence : 0;
  const r = live ? { ...sel.rec, presence: live } : sel.rec;
  const worked = cells.filter((c) => c.inMonth && ['ok', 'short', 'halfLeave'].includes(c.rec.status));
  const stdH = Math.max(1, ...cells.map((c) => c.rec.standardHours));
  const mix = [
    { k: 'Đủ công', n: kpi.mix.full, c: LOOK.ok.bar },
    { k: 'Thiếu', n: kpi.mix.short, c: LOOK.short.bar },
    { k: 'Phép', n: kpi.mix.leave, c: LOOK.leave.bar },
    { k: 'Lễ', n: kpi.mix.holiday, c: LOOK.holiday.bar },
  ];
  const mixTotal = Math.max(1, mix.reduce((a, x) => a + x.n, 0));
  const kpis = [
    { k: 'Công chuẩn', v: num(kpi.stdDays), u: 'ngày', tip: 'Số ngày công chuẩn của tháng theo lịch công ty · ngày lễ vẫn tính công chuẩn' },
    { k: 'Công thực tế', v: num(Math.round(kpi.actualDays * 10) / 10), u: 'ngày', tip: 'Tổng giờ có mặt trong ca ÷ giờ ngày thường · phép tính riêng' },
    { k: 'Đi muộn', v: String(kpi.lateDays), u: `${kpi.lateMinutes} phút`, warn: kpi.lateDays > 0, tip: 'So giờ vào với đầu ca theo lịch công ty' },
    { k: 'Ngoài giờ', v: num(Math.round(kpi.otHours * 10) / 10), u: 'giờ', tip: 'Giờ có mặt vượt giờ chuẩn ngày · cần đơn tăng ca được duyệt mới tính OT' },
  ];
  const look = LOOK[r.status];
  const rows = [
    { k: 'Vào', v: r.in || '—', warn: r.lateMinutes > 0 },
    { k: 'Ra', v: r.out || '—', warn: r.status === 'short' && !r.out },
    { k: 'Có mặt', v: r.presence ? hours(r.presence) : '—', tip: 'Có mặt · trừ nghỉ trưa theo lịch công ty' },
    { k: 'Đi muộn', v: r.lateMinutes ? `${r.lateMinutes} phút` : '—', warn: r.lateMinutes > 0 },
  ];
  const src = r.status === 'leave' || r.status === 'halfLeave' ? (r.status === 'leave' ? 'Phép năm · cả ngày' : 'Phép năm · nửa ngày sáng')
    : r.status === 'holiday' ? 'Ngày lễ theo lịch công ty'
    : r.status === 'off' ? 'Ngày nghỉ theo lịch tuần'
    : 'Passkey · trong bán kính VP Cộng Hòa';

  return (
    <div className={s.page}>
      <div className={s.kpis}>
        {kpis.map((k) => (
          <div key={k.k} className={s.card} title={k.tip}>
            <span className={s.kpiLb}>{k.k}</span>
            <span className={s.kpiVal}><b data-warn={k.warn ? 1 : undefined}>{k.v}</b><span>{k.u}</span></span>
          </div>
        ))}
      </div>
      <div className={s.grid2t}>
        <section className={s.card} aria-label="Lịch chấm công tháng">
          <div className={s.head}>
            <b>Tháng {ym.slice(5)}/{ym.slice(0, 4)}</b>
            <span className={s.grow} />
            <span className={s.legendRow}>
              {LEGEND.map(([k, c]) => (
                <span key={k}><i className={s.sq} style={{ background: c }} />{k}</span>
              ))}
            </span>
          </div>
          <div className={s.cal} role="grid" aria-label="Lịch chấm công">
            {DOWS.map((d) => (
              <div key={d} className={s.calDow} role="columnheader">{d}</div>
            ))}
            {cells.map((c) => {
              if (!c.inMonth) return <span key={c.day} aria-hidden="true" />;
              const st = c.rec.status;
              const soft = st === 'off' || st === 'future';
              const on = c.day === sel.day;
              const mark = MARK[st];
              const lab = c.rec.presence > 0 ? h1(c.rec.presence) : st === 'leave' ? 'phép' : '';
              return (
                <button
                  key={c.day}
                  type="button"
                  role="gridcell"
                  className={s.calCell}
                  data-soft={soft ? 1 : undefined}
                  data-future={st === 'future' ? 1 : undefined}
                  data-today={c.day === m.today ? 1 : undefined}
                  aria-pressed={on}
                  disabled={st === 'future'}
                  aria-label={`${dowOf(c.day)} ${dm(c.day)} · ${LOOK[st].k || 'chưa tới'}`}
                  onClick={() => setPick(c.day)}
                >
                  <span className={s.calTop}><b>{Number(c.day.slice(8))}</b>{mark && <i data-warn={st === 'short' ? 1 : undefined}>{mark}</i>}</span>
                  <span className={s.calBot}><i className={s.calBar} style={{ background: LOOK[st].bar }} /><span>{lab}</span></span>
                </button>
              );
            })}
          </div>
        </section>
        <div className={s.stack}>
          <section className={s.card} aria-label="Chi tiết ngày">
            <div className={s.head}>
              <b>{fdw(r.day)}</b>
              <span className={s.grow} />
              <span className={s.pill} style={{ background: look.chipBg ?? 'var(--sub)', color: look.chipTx ?? 'var(--tx2)' }}><i className={s.dot} style={{ background: look.bar }} />{look.k || 'Chưa tới'}</span>
            </div>
            <div className={s.kv2}>
              {rows.map((x) => (
                <div key={x.k} title={x.tip}><span>{x.k}</span><b data-warn={x.warn ? 1 : undefined}>{x.v}</b></div>
              ))}
            </div>
            <div className={s.src}><IconMapPin size={14} aria-hidden="true" />{src}</div>
            {r.status === 'short' && (
              <Button variant="secondary" icon={<IconMessageReport size={16} />} onClick={() => fix(r.day)}>{t('mc.explain')}</Button>
            )}
          </section>
          <section className={s.card} aria-label={t('mc.monthMix')}>
            <div className={s.head}><b>{t('mc.mix')}</b></div>
            <div className={s.mix} role="img" aria-label={mix.map((x) => `${x.k} ${x.n}`).join(', ')}>
              {mix.filter((x) => x.n > 0).map((x) => (
                <div key={x.k} style={{ width: `${(x.n / mixTotal) * 100}%`, background: x.c }} />
              ))}
            </div>
            <div className={s.mixLeg}>
              {mix.map((x) => (
                <span key={x.k}><i className={s.sq} style={{ background: x.c }} />{x.k}<b>{x.n}</b></span>
              ))}
            </div>
          </section>
          <section className={s.card} aria-label={t('mc.hoursByDay')}>
            <div className={s.head}><b>{t('mc.hours')}</b><span className={s.info} title={t('mc.hoursTip')}>ⓘ</span></div>
            <div className={s.spark} role="img" aria-label={`Giờ có mặt ${worked.length} ngày`}>
              <i className={s.sparkStd} style={{ bottom: `${Math.round((stdH / SPARK_MAX) * 100)}%` }} />
              {worked.map((c) => (
                <div
                  key={c.day}
                  title={`${dm(c.day)} · ${hours(c.rec.presence)}`}
                  style={{ height: `${Math.round(Math.min(1, c.rec.presence / SPARK_MAX) * 100)}%`, background: c.rec.status === 'short' ? 'var(--d-amber)' : c.rec.presence > c.rec.standardHours + 0.25 ? 'var(--pri)' : 'var(--d-blue)' }}
                />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export function TimeScreen() {
  const m = useMee();
  const toast = useToast();
  const [dr, setDr] = useState<{ day: string } | null>(null);
  const [ym, setYm] = useState(m.today.slice(0, 7));
  const out = m.attendance.find((a) => a.day === m.today)?.out ?? '';
  return (
    <>
      <PageHeader
        title="Chấm công"
        sub={m.me ? `${m.me.name} · ${m.me.dept}` : undefined}
        tabs={
          m.state === 'ready' ? (
            <Segmented
              label="Tháng"
              value={ym}
              onChange={setYm}
              items={[shiftMonth(m.today.slice(0, 7), -1), m.today.slice(0, 7)].map((k) => ({ key: k, label: `${k.slice(5)}/${k.slice(0, 4)}` }))}
            />
          ) : undefined
        }
        actions={
          m.state === 'ready' ? (
            <Button variant="primary" icon={<IconLogout size={18} />} disabled={!!out} onClick={() => toast({ msg: `Đã chấm ra ${m.now}`, undo: m.clockOut(m.now) })}>
              {out ? `Đã chấm ra ${out}` : 'Chấm công ra'}
            </Button>
          ) : undefined
        }
      />
      <StateCard state={m.state}>
        <StateView
          state={m.state}
          {...meeStateProps('time', m.state, m.retry, () => toast({ msg: 'Đã mở Chấm công vào' }))}
          skeleton={<div className={s.skel}><Skeleton rows={3} /><Skeleton rows={6} /></div>}
        >
          <TimeBody key={ym} m={m} ym={ym} fix={(day) => setDr({ day })} />
          <RequestDrawer open={!!dr} onOpenChange={(o) => !o && setDr(null)} m={m} kind="fix" day={dr?.day} />
        </StateView>
      </StateCard>
    </>
  );
}
